import React, { useEffect, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaMicrophone,
  FaStop,
  FaStopwatch,
} from 'react-icons/fa';
import Webcam from 'react-webcam';
import { useNavigate, useParams } from 'react-router-dom';
import { FormInputs } from '../../../types/session-forms.types';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import 'firebase/storage';
import { Collections } from '../../../types/collection';
import api from '../../../libs/axiosInstance';
import * as faceapi from 'face-api.js';
import { Toast } from 'primereact/toast';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useUserContext } from '../../../contexts/user.context';
import { INTERVIEW_TYPES } from '../../../pages/NewSession';
import { RunData } from '../../../interfaces/run.interface';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { useWindowSize } from 'react-use';
import { captureException } from '@sentry/react';
import { apiUsageService } from '../../../services/api-usage.service';
import TalkingPointsView from '../components/TalkingPoints';
import { sessionCompletionService } from '../../../services/session-completion.service';
import GoalSuggestionModal from '../../../components/GoalManagement/GoalSuggestionModal';
// import { GoalSuggestion } from '../../../services/ai-goal-suggestions.service';
import { TalkingPoint } from '../../../interfaces/talking-point.interface';
import { useMediaQuery } from 'react-responsive';
import { Dialog } from 'primereact/dialog';
import { FaCircleInfo } from 'react-icons/fa6';
import { HiSparkles } from 'react-icons/hi2';

export interface ExpressionData {
  angry: number;
  disgusted: number;
  fearful: number;
  happy: number;
  neutral: number;
  sad: number;
  surprised: number;
}

// SpeechRecognition shim
const SpeechRecognitionConstructor =
  window.SpeechRecognition || (window as any).webkitSpeechRecognition;

// Detect iOS device helper
const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

const TimedPitchActiveSession: React.FC = () => {
  const { user, reloadUserData } = useUserContext();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { sessionId, runId } = useParams();

  // Basic session data
  const [sessionData, setSessionData] = useState<FormInputs>();
  const sessionType = 'video';

  // Transcript & speech recognition
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Media recording
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);
  const recordedBlobsRef = useRef<Blob[]>([]);

  // Face expression analysis
  const [expressions, setExpressions] = useState<ExpressionData[]>([]);
  const analysisIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Talking points
  const [talkingPoints, setTalkingPoints] = useState<TalkingPoint[]>([]);

  // UI states
  const [showRunCompleteDialog, toggleShowRunCompleteDialog] = useState(false);
  const [submitting, toggleSubmitting] = useState(false);

  // For confetti sizing
  const { width, height } = useWindowSize();

  // Recording length constraints
  const MAX_RECORDING_TIME_MS =
    Number(user?.subscription.recordingLength) * 60 * 1000;
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(
    MAX_RECORDING_TIME_MS / 1000
  );
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // For responsive design
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [showMobileTalkingPoints, setShowMobileTalkingPoints] = useState(false);

  // Goal suggestions (temporarily disabled)
  const [showGoalSuggestions, setShowGoalSuggestions] = useState(false);
  // const [goalSuggestions, setGoalSuggestions] = useState<GoalSuggestion[]>([]);

  /* -----------------------------------------------------------------
   * Utilities
   * ----------------------------------------------------------------- */
  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  /* -----------------------------------------------------------------
   * On mount: reload user data, load face-api, fetch session data, etc.
   * ----------------------------------------------------------------- */
  useEffect(() => {
    reloadUserData();
  }, [reloadUserData]);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await faceapi.loadTinyFaceDetectorModel(MODEL_URL);
        await faceapi.loadFaceExpressionModel(MODEL_URL);
      } catch (err) {
        captureException(err);
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    async function getSessionData() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId || !sessionId) return;
        const sessionObjectSnap = await firebase
          .firestore()
          .collection(Collections.Sessions)
          .doc(userId)
          .collection('list')
          .doc(sessionId)
          .get();
        if (!sessionObjectSnap.exists) return;
        const data = sessionObjectSnap.data() as FormInputs;
        setSessionData({ ...data, id: sessionObjectSnap.id });
      } catch (error) {
        captureException(error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load session data.',
        });
      }
    }
    getSessionData();
  }, [sessionId]);

  useEffect(() => {
    async function getRunData() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId || !sessionId || !runId) return;
        firebase
          .firestore()
          .collection(Collections.Runs)
          .doc(sessionId)
          .collection('list')
          .doc(runId)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) return;
            const runData = snapshot.data() as RunData;
            if (runData.completed) {
              toggleShowRunCompleteDialog(true);
            }
            if (runData?.talkingPoints) {
              setTalkingPoints(runData.talkingPoints);
            }
          });
      } catch (error) {
        captureException(error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load run data.',
        });
      }
    }
    getRunData();
  }, [sessionId, runId]);

  /* -----------------------------------------------------------------
   * CLEANUP: Stop all media streams on component unmount
   * This prevents the camera indicator from staying on after navigation
   * ----------------------------------------------------------------- */
  useEffect(() => {
    return () => {
      // Stop webcam stream
      if (webcamRef.current?.video?.srcObject) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }

      // Stop media recorder if active
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }

      // Stop speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }

      // Clear all intervals
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, [mediaRecorder]);

  /* -----------------------------------------------------------------
   * Initialize SpeechRecognition
   * ----------------------------------------------------------------- */
  useEffect(() => {
    if (!SpeechRecognitionConstructor) {
      captureException('SpeechRecognition is not supported in this browser.');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Speech recognition is not supported in your browser.',
      });
      return;
    }

    const recognition = new SpeechRecognitionConstructor();
    // Some mobile browsers do not handle continuous well — especially iOS
    recognition.continuous = !isIOS();
    recognition.interimResults = true;
    recognition.lang = window.navigator.language || 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptChunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptChunk;
        } else {
          interimTranscript += transcriptChunk;
        }
      }
      setTranscript(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      captureException(event.error);
      toast.current?.show({
        severity: 'error',
        summary: 'Speech Recognition Error',
        detail: `Error: ${event.error}`,
      });
    };

    // If iOS or if we want robust fallback, we manually restart onend:
    recognition.onend = () => {
      if (isListening) {
        recognition.start();
      }
    };

    recognitionRef.current = recognition;
  }, []);

  /* -----------------------------------------------------------------
   * Start / Stop recording and speech recognition
   * ----------------------------------------------------------------- */
  const startRecognition = () => {
    setExpressions([]);
    recordedBlobsRef.current = [];
    setTranscript('');
    setVideoUrl(null);

    // If speech recognition is available and not currently listening, start it
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
      setIsListening(true);
    }

    // Start media recording
    if (!isRecording) {
      const stream = webcamRef.current?.stream;
      if (!stream) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No webcam stream available.',
        });
        return;
      }

      let options: MediaRecorderOptions = { mimeType: 'video/mp4' };
      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        options = { mimeType: 'video/webm; codecs=vp8' };
        // console.warn('MP4 not supported, using WebM instead.');
      }

      try {
        const recorder = new MediaRecorder(stream, options);
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            recordedBlobsRef.current.push(event.data);
          }
        };
        recorder.onstop = () => {
          const superBuffer = new Blob(recordedBlobsRef.current, {
            type: recorder.mimeType,
          });
          const videoURL = window.URL.createObjectURL(superBuffer);
          setVideoUrl(videoURL);
          setMediaRecorder(null);
        };

        recorder.start(100); // record in small chunks
        setMediaRecorder(recorder);
        setIsRecording(true);

        // Start analyzing expressions
        startExpressionAnalysis();

        // Clear intervals/timeouts
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }

        // Reset countdown
        setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

        // (A) Update timer every second
        recordingIntervalRef.current = setInterval(() => {
          setRecordingTimeLeft((prev) => {
            if (prev <= 1) {
              stopRecognition();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // (B) Force-stop after max duration
        recordingTimeoutRef.current = setTimeout(() => {
          stopRecognition();
          stopExpressionAnalysis();
          submitAnswer();
        }, MAX_RECORDING_TIME_MS);
      } catch (err) {
        captureException(err);
        toast.current?.show({
          severity: 'error',
          summary: 'Recording Error',
          detail: 'Failed to start recording. Please try again.',
        });
      }
    }
  };

  const stopRecognition = () => {
    // Clear intervals/timeouts
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

    // Stop speech recognition
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    // Stop MediaRecorder
    if (isRecording && mediaRecorder) {
      mediaRecorder.requestData();
      mediaRecorder.stop();
      setIsRecording(false);
    }

    // Stop analyzing expressions
    stopExpressionAnalysis();
  };

  /* -----------------------------------------------------------------
   * Start / Stop Expression Analysis
   * ----------------------------------------------------------------- */
  const startExpressionAnalysis = () => {
    if (analysisIntervalRef.current) return; // already running
    analysisIntervalRef.current = setInterval(async () => {
      if (
        webcamRef.current?.video &&
        webcamRef.current.video.readyState === 4 &&
        faceapi.nets.tinyFaceDetector.params
      ) {
        const detections = await faceapi
          .detectAllFaces(
            webcamRef.current.video,
            new faceapi.TinyFaceDetectorOptions()
          )
          .withFaceExpressions();
        if (detections && detections.length > 0) {
          setExpressions((prev) => [
            ...prev,
            detections[0].expressions as ExpressionData,
          ]);
        }
      }
    }, 1000);
  };

  const stopExpressionAnalysis = () => {
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
  };

  /* -----------------------------------------------------------------
   * Submit Answer
   * ----------------------------------------------------------------- */
  const submitAnswer = async () => {
    try {
      if (!sessionData) return;
      toast.current?.show({
        severity: 'info',
        summary: 'Please wait...',
        detail: 'Saving your feedback',
      });

      const userId = firebase.auth().currentUser?.uid;
      if (!userId) {
        toast.current?.show({
          severity: 'error',
          summary: 'Authentication Error',
          detail: 'User not logged in.',
        });
        return;
      }

      const finalBlob = new Blob(recordedBlobsRef.current, {
        type: mediaRecorder?.mimeType || 'video/webm',
      });

      if (finalBlob.size === 0) {
        toast.current?.show({
          severity: 'warn',
          summary: '🎤 No Recording Found',
          detail: 'Please record your pitch before submitting. Use the record button to capture your presentation.',
          life: 4000,
        });
        return;
      }
      toggleSubmitting(true);

      const formData = new FormData();
      formData.append('file', finalBlob, 'recording.mp4');
      formData.append('transcript', transcript);
      formData.append('expressions', JSON.stringify(expressions));
      formData.append('userId', userId);
      formData.append('runId', String(runId));
      formData.append('sessionId', String(sessionData.id));
      formData.append('interviewType', INTERVIEW_TYPES.TIMED_PITCH_SESSION);
      formData.append('sessionTitle', sessionData.title);

      const response = await api.post('/ai/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if ([200, 201].includes(response.status)) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Analyzing your response and generating feedback',
        });
        toggleSubmitting(false);
        endSession();
      } else {
        toggleSubmitting(false);
        throw new Error('Unexpected response status.');
      }
      toggleSubmitting(false);
    } catch (error) {
      toggleSubmitting(false);
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Submission Error',
        detail: 'Could not submit your answer. Please try again.',
      });
    }
  };

  /* -----------------------------------------------------------------
   * End Session
   * ----------------------------------------------------------------- */
  const endSession = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId || !sessionId || !runId) return;
      toggleShowRunCompleteDialog(true);

      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({
          completed: true,
          status: 'Completed',
          currentQuestion: 0,
        });

      await firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc(userId)
        .collection('list')
        .doc(sessionId)
        .update({
          completed: true,
          status: 'Completed',
        });

      // Increment practice attempt usage after completing the full session
      await apiUsageService.incrementPracticeAttemptUsage();

      // Handle goal progress updates from session completion
      try {
        await sessionCompletionService.notifySessionCompletion({
          sessionId: sessionId,
          userId: userId,
          runId: runId,
          overallScore: 0, // This should be updated with actual score when available
          sessionType: 'timed_pitch_session',
          completedAt: new Date(),
          skills: {} // Can be populated with actual skill scores
        });

        // Generate goal suggestions after session completion
        try {
          // Goal suggestions generation temporarily disabled
          // const suggestions = await sessionCompletionService.generateGoalSuggestionsFromSession({
          //   sessionId: sessionId,
          //   sessionTitle: sessionData?.title || 'Timed Pitch Session',
          //   sessionType: 'timed-pitch',
          //   score: 0,
          //   completedAt: new Date().toISOString(),
          //   userId: userId
          // });

          // if (suggestions.length > 0) {
          //   setGoalSuggestions(suggestions);
          //   setShowGoalSuggestions(true);
          // }
        } catch (suggestionError) {
          console.error('Error generating goal suggestions:', suggestionError);
          // Don't block session completion if suggestion generation fails
        }
      } catch (goalError) {
        console.error('Error updating goals from session completion:', goalError);
        // Don't block session completion if goal update fails
      }

      setTranscript('');
      setVideoUrl(null);
    } catch (error) {
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to end the session. Please try again.',
      });
    }
  };

  /* -----------------------------------------------------------------
   * MAIN RENDER
   * ----------------------------------------------------------------- */

  if (submitting) {
    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen bg-gradient-to-tl from-blue-200 to-purple-200">
        <DotLottieReact
          src="https://lottie.host/f7b5bebc-ce92-4136-9295-815dea9c1920/P0BpzInq7K.lottie"
          loop
          autoplay
          className="w-32 h-32"
        />
        <div className="mt-2 text-gray-700">
          <small>Uploading...</small>
        </div>
      </div>
    );
  }

  if (!sessionData) return null;

  /* ------------------------------
   * MOBILE LAYOUT
   * ------------------------------ */
  if (isMobile) {
    return (
      <div className="relative w-screen h-screen overflow-hidden bg-black">
        {/* The camera feed filling the screen */}
        <Webcam
          audio
          ref={webcamRef}
          muted
          mirrored
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Dark overlay to enhance contrast */}
        <div className="absolute inset-0 bg-black bg-opacity-40" />

        {/* The overlayed content */}
        <div className="relative z-10 flex flex-col justify-between h-full">
          {/* Top bar */}
          <header className="flex items-center justify-between px-4 py-3 bg-transparent">
            {/* Back button */}
            <motion.button
              onClick={() => navigate(-1)}
              className="flex items-center text-white hover:text-gray-200 transition"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="mr-2" />
              <span className="font-semibold">Back</span>
            </motion.button>

            {/* Session Title */}
            <h1 className="text-lg font-bold text-white truncate">
              {sessionData.title}
            </h1>

            {/* End session button */}
            <motion.button
              onClick={endSession}
              className="bg-red-600 px-3 py-1 rounded-md text-white hover:bg-red-700 transition text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              End
            </motion.button>
          </header>

          {/* Middle area: transcript snippet & timer */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 space-y-4">
            {/* Show time left if recording */}
            {isRecording && (
              <motion.div
                className="font-semibold text-white flex items-center text-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <FaStopwatch className="mr-2 text-red-500 animate-pulse" />
                {formatTime(recordingTimeLeft)}
              </motion.div>
            )}

            {/* Transcript snippet (no scrolling) */}
            <div className="bg-white bg-opacity-80 text-gray-800 p-3 rounded-md max-w-full w-full">
              <p className="text-sm line-clamp-3 overflow-hidden" title={transcript}>
                {transcript || 'Your speech will appear here...'}
              </p>
            </div>
          </div>

          {/* Bottom bar with talking points toggle and record controls */}
          <div className="p-4 bg-gradient-to-t from-black/70 to-transparent flex flex-col space-y-4">
            <button
              onClick={() => setShowMobileTalkingPoints(true)}
              className="bg-gray-200 bg-opacity-80 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-md flex items-center justify-center"
            >
              <FaCircleInfo className="mr-2" />
              <span className="text-sm font-semibold">Show Talking Points</span>
            </button>

            <div className="flex items-center justify-center space-x-4">
              {/* Start/Stop */}
              {!isRecording && sessionType === 'video' && (
                <motion.button
                  onClick={startRecognition}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaMicrophone className="mr-2" />
                  Start
                </motion.button>
              )}
              {isRecording && (
                <motion.button
                  onClick={stopRecognition}
                  className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaStop className="mr-2" />
                  Stop
                </motion.button>
              )}

              {/* Submit button only if we have a recording available */}
              {videoUrl && !isRecording && (
                <motion.button
                  onClick={submitAnswer}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Submit
                </motion.button>
              )}
            </div>
          </div>
        </div>

        <Toast ref={toast} />

        {/* COMPLETION OVERLAY */}
        <AnimatePresence>
          {showRunCompleteDialog && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Confetti */}
              <div className="absolute inset-0 pointer-events-none">
                <Confetti width={width} height={height} />
              </div>

              <motion.div
                className="relative bg-white rounded-lg shadow-lg p-8 text-center flex flex-col items-center max-w-md w-full"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <DotLottieReact
                  src="https://lottie.host/392d3f6c-7239-46fc-9b41-ca5a9814504d/hFXbyazAMW.lottie"
                  loop
                  autoplay
                  className="w-40 h-40 mx-auto mb-4"
                />
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Session Complete!
                </h2>
                <p className="text-gray-600 mb-6">
                  Congratulations on completing your session. We’ll analyze your
                  responses and compile your feedback shortly.
                </p>
                <motion.button
                  onClick={() => navigate(-1)}
                  className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Go Back
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* TALKING POINTS MODAL */}
        <Dialog
          header={
            <div className="flex items-center gap-2">
              <HiSparkles className="text-purple-600" />
              <span>Your Talking Points</span>
            </div>
          }
          visible={showMobileTalkingPoints}
          onHide={() => setShowMobileTalkingPoints(false)}
          style={{ width: '90vw', maxWidth: '600px' }}
        >
          <TalkingPointsView data={talkingPoints || []} />
        </Dialog>
      </div>
    );
  }

  /* ------------------------------
   * DESKTOP / TABLET LAYOUT
   * ------------------------------ */
  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-tr from-blue-50 to-purple-50 relative">
      <Toast ref={toast} />

      {/* HEADER */}
      <header className="flex-none bg-gradient-to-r from-purple-600 to-blue-500 text-white p-4 flex items-center justify-between">
        {/* Back Button */}
        <motion.button
          onClick={() => navigate(-1)}
          className="flex items-center text-white hover:text-gray-200 transition"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={submitting}
        >
          <FaArrowLeft className="mr-2" />
          Back
        </motion.button>

        {/* Title */}
        <motion.h1
          className="text-lg md:text-xl font-bold text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Interview Session: {sessionData?.title}
        </motion.h1>

        {/* Session Timer & End Button */}
        <div className="flex items-center space-x-4">
          <div className="hidden lg:flex items-center">
            <FaStopwatch className="mr-2" />
            <p className="font-semibold">
              {sessionData?.duration?.label || ''}
            </p>
          </div>
          <motion.button
            onClick={endSession}
            className="bg-red-500 px-4 py-2 rounded-lg hover:bg-red-600 transition flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={submitting}
          >
            <small>End Session</small>
          </motion.button>
        </div>
      </header>

      {/* MAIN CONTENT - responsive columns */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto overflow-x-hidden min-w-0">
        {/* LEFT COLUMN: Talking points */}
        <motion.section
          className="lg:w-1/2 p-4 md:p-6 flex-shrink-0 flex-grow-0 min-w-0"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-lg shadow-lg p-4 h-full flex flex-col">
            <h2 className="text-xl font-bold text-gray-800">Your Talking Points</h2>
            <motion.div
              className="mt-2 h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1 }}
            />

            {/* We can truncate or show partially if needed */}
            <div className="mt-4 flex-1 overflow-auto min-w-0">
              <TalkingPointsView data={talkingPoints || []} />
            </div>
          </div>
        </motion.section>

        {/* RIGHT COLUMN: Camera + Controls */}
        <motion.section
          className="lg:flex-1 p-4 md:p-6 flex flex-col min-w-0"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col h-full">
            {/* CAMERA FEED */}
            <div className="mb-4 flex-auto min-h-0 flex flex-col overflow-hidden">
              <label className="text-lg font-bold mb-2 text-gray-800">
                Camera Feed
              </label>
              <div className="relative flex-1 min-h-0 overflow-hidden rounded-lg">
                <Webcam
                  audio
                  ref={webcamRef}
                  muted
                  mirrored
                  className="w-full h-full object-cover"
                />
              </div>

              {/* If there's a recorded video, preview below */}
              {videoUrl && (
                <div className="mt-3">
                  <h3 className="text-lg font-bold text-gray-800">
                    Recorded Video Preview:
                  </h3>
                  <video
                    src={videoUrl}
                    controls
                    className="w-full mt-2 rounded-lg"
                  />
                </div>
              )}
            </div>

            {/* CONTROLS: Start/Stop & Transcript & Submit */}
            <div className="flex-none">
              <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
                {/* Recording / Timer Buttons */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex space-x-4">
                    {!isRecording && sessionType === 'video' && (
                      <motion.button
                        onClick={startRecognition}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={submitting}
                      >
                        <FaMicrophone className="mr-2" />
                        Start
                      </motion.button>
                    )}
                    {isRecording && (
                      <motion.button
                        onClick={stopRecognition}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={submitting}
                      >
                        <FaStop className="mr-2" />
                        Stop
                      </motion.button>
                    )}
                  </div>

                  {/* Timer */}
                  {isRecording && (
                    <motion.div
                      className="font-semibold text-gray-800 flex items-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <FaStopwatch className="mr-2 text-red-600 animate-pulse" />
                      {formatTime(recordingTimeLeft)}
                    </motion.div>
                  )}
                </div>

                {/* Transcript snippet */}
                <div className="mt-4">
                  <p className="font-bold text-gray-800 mb-1">Transcript:</p>
                  <p
                    className="text-sm text-gray-700 line-clamp-3 overflow-hidden"
                    title={transcript}
                  >
                    {transcript || 'Your speech will appear here...'}
                  </p>
                </div>

                {/* Submit Button */}
                {videoUrl && !isRecording && (
                  <div className="flex justify-end mt-4">
                    <motion.button
                      onClick={submitAnswer}
                      className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition font-semibold"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={submitting}
                    >
                      Submit
                    </motion.button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.section>
      </div>

      {/* COMPLETION OVERLAY */}
      <AnimatePresence>
        {showRunCompleteDialog && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Confetti */}
            <div className="absolute inset-0 pointer-events-none">
              <Confetti width={width} height={height} />
            </div>

            <motion.div
              className="relative bg-white rounded-lg shadow-lg p-8 text-center flex flex-col items-center max-w-md w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              <DotLottieReact
                src="https://lottie.host/392d3f6c-7239-46fc-9b41-ca5a9814504d/hFXbyazAMW.lottie"
                loop
                autoplay
                className="w-40 h-40 mx-auto mb-4"
              />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Session Complete!
              </h2>
              <p className="text-gray-600 mb-6">
                Congratulations on completing your session. We’ll analyze your
                responses and compile your feedback shortly.
              </p>
              <motion.button
                onClick={() => navigate(-1)}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition font-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={submitting}
              >
                Go Back
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Goal Suggestions Modal */}
      <GoalSuggestionModal
        visible={showGoalSuggestions}
        onHide={() => setShowGoalSuggestions(false)}
        suggestions={[]}
        sessionTitle={sessionData?.title || 'Timed Pitch Session'}
        onGoalCreated={() => {
          // Optionally refresh goals or show success message
        }}
      />
    </div>
  );
};

export default TimedPitchActiveSession;
